// ============================================================================
// FILE UPLOAD ZONE
// ============================================================================
// Reusable drag/drop + click-to-select file input. First PRIMA file-upload
// component — kept small. No progress bar yet (upload progress is handled
// by the consuming service's loading flag).

import React, { useCallback, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';

interface Props {
  /** Comma-separated accept attribute (e.g. ".xlsx,.csv,.xml"). */
  accept?: string;
  /** Hint shown inside the drop zone (e.g. "Excel, CSV or Tally XML"). */
  hint?: string;
  /** Selected file (controlled). */
  value: File | null;
  /** Called when the user picks / drops a new file. */
  onChange: (file: File | null) => void;
  /** Optional max file size (bytes); shows an error toast if exceeded. */
  maxSizeBytes?: number;
  /** Disabled state — drop / click do nothing. */
  disabled?: boolean;
}

const FileUploadZone: React.FC<Props> = ({
  accept,
  hint = 'Drop a file here, or click to browse',
  value,
  onChange,
  maxSizeBytes,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accepted = useCallback((file: File): boolean => {
    if (maxSizeBytes && file.size > maxSizeBytes) {
      setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max ${(maxSizeBytes / 1024 / 1024).toFixed(0)} MB.`);
      return false;
    }
    setError(null);
    return true;
  }, [maxSizeBytes]);

  const handlePick = useCallback((file: File | null) => {
    if (!file) {
      onChange(null);
      setError(null);
      return;
    }
    if (accepted(file)) onChange(file);
  }, [accepted, onChange]);

  return (
    <div>
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => { if (disabled) return; e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) handlePick(f);
        }}
        style={{
          border: `2px dashed ${dragOver ? '#2563eb' : 'var(--form-border-color)'}`,
          borderRadius: 8,
          padding: '1.5rem',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: dragOver ? '#eff6ff' : 'var(--bg-secondary)',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.15s',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
          style={{ display: 'none' }}
        />
        {value ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <IconSystem.file size={20} />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{value.name}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {(value.size / 1024).toFixed(1)} KB · {value.type || 'unknown'}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handlePick(null); }}
              style={{
                background: 'transparent',
                border: '1px solid var(--form-border-color)',
                borderRadius: 4,
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div>
            <IconSystem.upload size={28} />
            <div style={{ marginTop: 8, fontSize: '0.85rem', fontWeight: 500 }}>{hint}</div>
            {accept && (
              <div style={{ marginTop: 4, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Accepted: {accept}
              </div>
            )}
          </div>
        )}
      </div>
      {error && (
        <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#dc2626' }}>{error}</div>
      )}
    </div>
  );
};

export default FileUploadZone;
